import { Application, Container, Graphics, Text } from "pixi.js";
import type { PlayerState, PointState, SnakeState } from "../types/state";

const FOOD_COLOR = "#EBA0AC";
const FOOD_GLOW = "#F2CDCD";
const GRID_COLOR = "#313244";
const BACKGROUND_COLOR = "#11111B";
const TEXT_COLOR = "#CDD6F4";

interface DrawPoint {
  x: number;
  y: number;
}

export class GameRenderer {
  private app: Application | null = null;
  private readonly stage = new Container();
  private readonly board = new Graphics();
  private readonly overlay = new Text({
    text: "",
    style: {
      fill: TEXT_COLOR,
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 24,
      fontWeight: "900",
      align: "center",
      stroke: { color: "#11111B", width: 4 }
    }
  });

  private current: SnakeState | null = null;
  private previous: SnakeState | null = null;
  private currentRef: SnakeState | null = null;
  private stateChangedAt = performance.now();

  public async mount(element: HTMLElement): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: element,
      backgroundAlpha: 0,
      antialias: false,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2)
    });

    element.innerHTML = "";
    element.appendChild(app.canvas);
    app.stage.addChild(this.stage);
    this.stage.addChild(this.board);
    this.stage.addChild(this.overlay);
    this.app = app;
  }

  public render(state: SnakeState | null): void {
    this.updateStateReference(state);
    if (!this.app) return;

    this.board.clear();
    const width = this.app.renderer.width;
    const height = this.app.renderer.height;

    this.drawBackground(width, height);
    if (!this.current) {
      this.drawOverlay("BATTLE CESD", width, height);
      return;
    }

    const view = this.current;
    const cell = this.cellSize(width, height, view.gridW, view.gridH);
    const offsetX = Math.floor((width - view.gridW * cell) / 2);
    const offsetY = Math.floor((height - view.gridH * cell) / 2);

    this.drawGrid(offsetX, offsetY, cell, view.gridW, view.gridH);
    this.drawFoods(view.foods, offsetX, offsetY, cell);

    const alpha = this.interpolationAlpha(view.tickMs);
    for (const player of view.players) this.drawPlayer(player, offsetX, offsetY, cell, alpha, view.gridW, view.gridH);

    if (view.phase === "lobby") this.drawOverlay("LOBBY", width, height);
    else if (view.phase === "gameover") this.drawOverlay("FIM DE JOGO", width, height);
    else this.overlay.text = "";
  }

  public destroy(): void {
    this.app?.destroy(true, { children: true, texture: true });
    this.app = null;
  }

  private updateStateReference(state: SnakeState | null): void {
    if (state === this.currentRef) return;

    this.previous = this.current;
    this.current = state ? this.cloneState(state) : null;
    this.currentRef = state;
    this.stateChangedAt = performance.now();
  }

  private interpolationAlpha(tickMs: number): number {
    const elapsed = performance.now() - this.stateChangedAt;
    const duration = Math.max(70, Math.min(180, tickMs));
    return Math.max(0, Math.min(1, elapsed / duration));
  }

  private drawBackground(width: number, height: number): void {
    this.board.rect(0, 0, width, height).fill({ color: this.hexToInt(BACKGROUND_COLOR), alpha: 1 });
  }

  private drawGrid(x: number, y: number, cell: number, cols: number, rows: number): void {
    const boardW = cols * cell;
    const boardH = rows * cell;

    this.board.roundRect(x - 3, y - 3, boardW + 6, boardH + 6, 12).fill({ color: 0x181825, alpha: 1 });
    this.board.rect(x, y, boardW, boardH).stroke({ width: 2, color: this.hexToInt(GRID_COLOR), alpha: 0.94 });

    for (let gx = 1; gx < cols; gx++) {
      const px = x + gx * cell;
      this.board.moveTo(px, y).lineTo(px, y + boardH).stroke({ width: 1, color: this.hexToInt(GRID_COLOR), alpha: 0.18 });
    }

    for (let gy = 1; gy < rows; gy++) {
      const py = y + gy * cell;
      this.board.moveTo(x, py).lineTo(x + boardW, py).stroke({ width: 1, color: this.hexToInt(GRID_COLOR), alpha: 0.18 });
    }
  }

  private drawFoods(foods: PointState[], offsetX: number, offsetY: number, cell: number): void {
    const pulse = 0.82 + Math.sin(Date.now() / 120) * 0.18;
    for (const food of foods) {
      const cx = offsetX + food.x * cell + cell / 2;
      const cy = offsetY + food.y * cell + cell / 2;
      const radius = Math.max(3, cell * 0.28);

      this.board.circle(cx, cy, radius * 1.55).fill({ color: this.hexToInt(FOOD_GLOW), alpha: 0.18 * pulse });
      this.board.circle(cx, cy, radius).fill({ color: this.hexToInt(FOOD_COLOR), alpha: 0.98 });
    }
  }

  private drawPlayer(player: PlayerState, offsetX: number, offsetY: number, cell: number, alpha: number, cols: number, rows: number): void {
    if (!player.joined || !player.alive) return;

    const color = this.hexToInt(player.hex);
    const headColor = this.lighten(color, 0.32);
    const previousPlayer = this.previous?.players.find((item) => item.i === player.i && item.alive);

    player.body.forEach((part, index) => {
      const point = this.interpolatePoint(previousPlayer?.body[index], part, alpha, cols, rows);
      const x = offsetX + point.x * cell;
      const y = offsetY + point.y * cell;
      const isHead = index === 0;
      const pad = isHead ? Math.max(1, cell * 0.06) : Math.max(1, cell * 0.13);
      const radius = Math.max(2, cell * (isHead ? 0.26 : 0.19));
      const partAlpha = isHead ? 1 : Math.max(0.5, 0.98 - index * 0.025);

      this.board
        .roundRect(x + pad, y + pad, cell - pad * 2, cell - pad * 2, radius)
        .fill({ color: isHead ? headColor : color, alpha: partAlpha });

      if (isHead && cell >= 14) this.drawEyes(x, y, cell, player.direction);
    });
  }

  private drawEyes(x: number, y: number, cell: number, direction: string): void {
    const eye = Math.max(1.5, cell * 0.09);
    const left = { x: x + cell * 0.35, y: y + cell * 0.35 };
    const right = { x: x + cell * 0.65, y: y + cell * 0.35 };

    if (direction === "down") {
      left.y = y + cell * 0.65;
      right.y = y + cell * 0.65;
    } else if (direction === "left") {
      left.x = x + cell * 0.35;
      left.y = y + cell * 0.35;
      right.x = x + cell * 0.35;
      right.y = y + cell * 0.65;
    } else if (direction === "right") {
      left.x = x + cell * 0.65;
      left.y = y + cell * 0.35;
      right.x = x + cell * 0.65;
      right.y = y + cell * 0.65;
    }

    this.board.circle(left.x, left.y, eye).fill({ color: 0x11111b, alpha: 0.95 });
    this.board.circle(right.x, right.y, eye).fill({ color: 0x11111b, alpha: 0.95 });
  }

  private interpolatePoint(oldPoint: PointState | undefined, newPoint: PointState, alpha: number, cols: number, rows: number): DrawPoint {
    if (!oldPoint) return { x: newPoint.x, y: newPoint.y };

    const dx = newPoint.x - oldPoint.x;
    const dy = newPoint.y - oldPoint.y;
    const wrapped = Math.abs(dx) > cols / 2 || Math.abs(dy) > rows / 2;
    if (wrapped) return { x: newPoint.x, y: newPoint.y };

    return {
      x: oldPoint.x + dx * alpha,
      y: oldPoint.y + dy * alpha
    };
  }

  private drawOverlay(text: string, width: number, height: number): void {
    this.overlay.text = text;
    this.overlay.anchor.set(0.5);
    this.overlay.x = width / 2;
    this.overlay.y = height / 2;
  }

  private cellSize(width: number, height: number, cols: number, rows: number): number {
    return Math.max(4, Math.floor(Math.min(width / cols, height / rows)));
  }

  private cloneState(state: SnakeState): SnakeState {
    return {
      ...state,
      players: state.players.map((player) => ({ ...player, body: player.body.map((point) => ({ ...point })) })),
      foods: state.foods.map((point) => ({ ...point })),
      colors: state.colors.map((color) => ({ ...color }))
    };
  }

  private hexToInt(hex: string): number {
    const normalized = hex.replace("#", "").trim();
    return Number.parseInt(normalized.length === 3 ? normalized.split("").map((c) => c + c).join("") : normalized, 16);
  }

  private lighten(color: number, amount: number): number {
    const r = Math.min(255, Math.round(((color >> 16) & 255) + 255 * amount));
    const g = Math.min(255, Math.round(((color >> 8) & 255) + 255 * amount));
    const b = Math.min(255, Math.round((color & 255) + 255 * amount));
    return (r << 16) | (g << 8) | b;
  }
}
